import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import compact from 'lodash-es/compact';

import type {
  AlgoliaProduct,
  ActiveFilters,
  JaneSearchState,
  MenuOptions,
} from '@jane/search/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Store, StoreSpecial } from '@jane/shared/models';
import type {
  Store as ZodStore,
  StoreSpecial as ZodStoreSpecial,
} from '@jane/shared/types';

import {
  formatActiveFilterLabel,
  formatActivePotencyFilterLabel,
} from './formatActiveFilterLabel';
import { getSpecialsLabels } from './getSpecialsLabels';

const arrayFilterKeys = [
  'activities',
  'aggregate_rating',
  'applicable_bulk_special_ids',
  'applicable_bundle_special_ids.discount', //discount and qualify arrays are always the same, so we only need to look at the discount array to build active filters for applicable_bundle_special_ids
  'applicable_cart_total_special_ids',
  'applicable_special_ids',
  'available_weights',
  'brand',
  'bucket_price',
  'category',
  'feelings',
  'has_brand_discount',
  'root_types',
  'compound_names',
] as const;

const rangeFilterKeys = ['percent_cbd', 'percent_thc'] as const;

/**
 * Translates product search state into array of active filter items:
 * @param {JaneSearchState<AlgoliaProduct>} [searchState] - The current search state.
 * @param {MenuOptions} [menuOptions] - Menu options encompass all customization options for store menus. Active filters take specials, customLabels and storeId into account.
 */

export const formatActiveFilters = <
  T extends Store | ZodStore = Store,
  U extends StoreSpecial | ZodStoreSpecial | V3StoreSpecial = StoreSpecial,
>(
  searchState: JaneSearchState<AlgoliaProduct>,
  menuOptions?: MenuOptions<T, U>
): ActiveFilters[] => {
  const { customLabels, specials, storeId } = menuOptions || {};
  const activeFilters = {
    ...searchState.bucketFilters,
    ...searchState.filters,
    ...searchState.rangeFilters,
  } as const;

  const specialsLabels = specials ? getSpecialsLabels<U>(specials) : undefined;

  // we use flatMap because formatActiveFilterLabel accepts and returns an array of items
  // ex: root_types, ['flower', 'vape'] ==> [{ label: 'Flower', value: 'flower', key: 'root_types' },
  // { label: 'Vape', value: 'vape', key: 'root_types' }]
  const formattedArrayFilters = arrayFilterKeys.flatMap((key) => {
    const filters = activeFilters[key as keyof typeof activeFilters];
    let customMenuLabels = undefined;

    if (key === 'category' || key === 'root_types') {
      customMenuLabels = customLabels ? customLabels[key] : undefined;
    }

    const presentedLabels = [
      'applicable_special_ids',
      'applicable_cart_total_special_ids',
      'applicable_bundle_special_ids.discount',
      'applicable_bulk_special_ids',
    ].includes(key)
      ? specialsLabels
      : customMenuLabels
        ? customMenuLabels
        : undefined;

    return Array.isArray(filters) && filters.length
      ? formatActiveFilterLabel(
          key === 'applicable_bundle_special_ids.discount'
            ? 'applicable_bundle_special_ids'
            : key,
          filters.map((value) => value.toString()),
          {
            storeId,
            customLabels: presentedLabels,
          }
        )
      : [];
  });

  const formattedPotencyFilters = rangeFilterKeys.map((key) => {
    const filter = activeFilters[key as keyof typeof activeFilters];

    return (
      filter &&
      !Array.isArray(filter) &&
      formatActivePotencyFilterLabel(key, filter)
    );
  });

  return compact([...formattedArrayFilters, ...formattedPotencyFilters]);
};

const dispensaryArrayFilterKeys = [
  'store_options',
  'current_deals',
  'ownership_identification',
] as const;

export const formatActiveDispensaryFilters = (
  searchState: JaneSearchState<any>
): ActiveFilters[] => {
  const activeFilters = {
    ...searchState.filters,
  };

  const formattedArrayFilters = dispensaryArrayFilterKeys.flatMap((key) => {
    const filters = activeFilters[key as keyof typeof activeFilters];

    return Array.isArray(filters) && filters.length
      ? formatActiveFilterLabel(
          key,
          filters.map((value) => value.toString())
        )
      : [];
  });

  return compact([...formattedArrayFilters]);
};
